import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator } from "lucide-react";

const VesselEmissionAnalytics = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const emissionData = [
    {
      id: "co2",
      title: "Carbon Dioxide (CO₂)",
      formula: "CO₂ (MT/day) = Σ(Daily Fuel (t) × CF_CO₂) / 1000",
      calculation: "For each fuel type: (HFO_daily_t × CF_C02_HFO) + (LFO_daily_t × CF_C02_LFO) + (MGO_daily_t × CF_C02_MGO) converted to MT",
      calculationDetails: [
        "HFO_daily_t = HFO_consumption_t / totalDays",
        "LFO_daily_t = LFO_consumption_t / totalDays",
        "MGO_daily_t = MGO_consumption_t / totalDays",
        "CF_C02 values from FuelTable for each fuel type",
        "Result: Metric tons per day (MT/day)"
      ],
      description: "Daily CO₂ emissions calculated from fuel consumption and fuel-specific emission factors.",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "ch4",
      title: "Methane (CH₄)",
      formula: "CH₄ (MT/day) = Σ(Daily Fuel (t) × CF_CH₄) / 1000",
      calculation: "For each fuel type: (HFO_daily_t × CF_CH4_HFO) + (LFO_daily_t × CF_CH4_LFO) + (MGO_daily_t × CF_CH4_MGO) converted to MT",
      calculationDetails: [
        "Uses CF_CH4 factors from FuelTable for each fuel",
        "Daily average consumption divided by total days in period",
        "Includes HFO, LFO, and MGO fuel types",
        "Result: Metric tons per day (MT/day)"
      ],
      description: "Daily methane emissions calculated from fuel consumption and specific CH₄ emission factors.",
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "n2o",
      title: "Nitrous Oxide (N₂O)",
      formula: "N₂O (MT/day) = Σ(Daily Fuel (t) × CF_N₂O) / 1000",
      calculation: "For each fuel type: (HFO_daily_t × CF_N20_HFO) + (LFO_daily_t × CF_N20_LFO) + (MGO_daily_t × CF_N20_MGO) converted to MT",
      calculationDetails: [
        "Uses CF_N20 factors from FuelTable for each fuel",
        "CF_N20 represents N₂O emission factor in g/kg fuel",
        "Daily average consumption based on selected time period",
        "Result: Metric tons per day (MT/day)"
      ],
      description: "Daily N₂O emissions calculated from fuel consumption and specific N₂O emission factors.",
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "co2equivalent",
      title: "CO₂ Equivalent (CO₂e) - Tank-to-Wake",
      formula: "CO₂e (MT/day) = CO₂ + (CH₄ × 28) + (N₂O × 298)",
      calculation: "Sum of all greenhouse gases weighted by GWP factors: (CO₂ × 1) + (CH₄ × 28) + (N₂O × 298)",
      calculationDetails: [
        "Global Warming Potential (GWP) factors:",
        "CH₄: 28 times stronger than CO₂",
        "N₂O: 298 times stronger than CO₂",
        "Represents Tank-to-Wake emissions only",
        "Used for absolute emission reporting"
      ],
      description: "Total climate impact of combustion emissions only, expressed as CO₂ equivalent.",
      additionalInfo: "Tank-to-Wake (TtW) includes only emissions from fuel combustion onboard the vessel.",
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ghg",
      title: "GHG Intensity - Well-to-Wake",
      formula: "GHG Intensity (g CO₂e/MJ) = Reward Factor × (WtT + TtW)",
      calculation: "Well-to-Tank (WtT) + Tank-to-Wake (TtW) emissions per unit of energy consumed",
      calculationDetails: [
        "WtT: Emissions from fuel production and transport (Well-to-Tank)",
        "TtW: Emissions from fuel combustion (Tank-to-Wake)",
        "Reward Factor: Currently 1.0",
        "Energy: Based on fuel LCV (Lower Calorific Value)",
        "Accounts for carbon slip and shore power energy"
      ],
      description: "Complete lifecycle emissions intensity, including fuel production, transport, and combustion.",
      additionalInfo: "Well-to-Wake (WtW) intensity provides complete lifecycle assessment per unit of energy.",
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "sox",
      title: "Sulfur Oxides (SOₓ)",
      formula: "SOₓ (MT/day) = [Σ(Fuel × Sulfur %) × 2 × 0.97753] / 100 / totalDays / 1000",
      calculation: "Sum of (FuelConsHSFO × SulHSFO) + (FuelConsLSFO × SulLSFO) + (FuelConsMDO × SulMDO) + (FuelConsLSMDO × SulLSMDO) with conversion",
      calculationDetails: [
        "Conversion factor: 2 × 0.97753 converts sulfur to SO₂",
        "Divided by 100 to convert % to decimal",
        "Divided by totalDays for daily average",
        "Divided by 1000 to convert kg to MT",
        "Uses sulfur content from vessel emission raw data"
      ],
      description: "Daily SOₓ emissions calculated from actual fuel sulfur content and consumption.",
      additionalInfo: "Based on HSFO, LSFO, MDO, and LSMDO fuel consumption with specific sulfur contents.",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "nox",
      title: "Nitrogen Oxides (NOₓ)",
      formula: "NOₓ (MT/day) = Σ(Fuel Consumption × NOₓ Factor) / totalDays / 1000",
      calculation: "Based on engine type, operational mode, and specific NOₓ emission factors",
      calculationDetails: [
        "Main Engine HSFO/LSFO: 78.46 g/kg fuel",
        "Main Engine MDO/LSMDO: 73.75 g/kg fuel",
        "Aux Engine HSFO/LSFO: 49.37 g/kg fuel",
        "Aux Engine MDO/LSMDO: 46.37 g/kg fuel",
        "Boiler HSFO/LSFO: 6.89 g/kg fuel",
        "Boiler MDO/LSMDO: 6.47 g/kg fuel",
        "Separate calculations for At Sea, Anchorage, and Port modes"
      ],
      description: "Daily NOₓ emissions calculated using comprehensive engine-specific and mode-specific factors.",
      additionalInfo: "Factors account for different engine types (ME, AE, BL) and operational modes.",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "pm",
      title: "Particulate Matter (PM)",
      formula: "PM (MT/day) = Σ[(Daily Fuel / SFOC) × PM10_Kwh] / 1000",
      calculation: "PM10_Kwh = 1.35 + SFOC × 7 × 0.02247 × (Sulfur %/100 - 0.0246)",
      calculationDetails: [
        "Uses SFOC (Specific Fuel Oil Consumption) values from SFOC table",
        "SFOC for HFO/LFO: ~200+ g/kWh (from HFO SSD data)",
        "SFOC for MGO: from MDO SSD data",
        "Sulfur content from latest vessel emission report",
        "PM calculation varies by fuel type and sulfur content"
      ],
      description: "Daily PM emissions calculated using SFOC-based formula with sulfur dependency.",
      additionalInfo: "Calculation differs for HFO/LFO vs MGO fuels based on different SFOC values.",
      borderColor: "border-gray-300",
      bgColor: "bg-gray-100",
      textColor: "text-gray-700",
    },
  ];

  const fuelFactorsInfo = {
    title: "Fuel Data & Emission Factors",
    description: "Emission calculations rely on fuel-specific data from FuelTable:",
    factors: [
      {
        fuel: "HFO",
        factors: "CF_C02, CF_CH4, CF_N20, LCV, CO2eq_WtT, CSlip, Fuel_Class"
      },
      {
        fuel: "LFO",
        factors: "CF_C02, CF_CH4, CF_N20, LCV, CO2eq_WtT, CSlip, Fuel_Class"
      },
      {
        fuel: "MGO",
        factors: "CF_C02, CF_CH4, CF_N20, LCV, CO2eq_WtT, CSlip, Fuel_Class"
      }
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Calculator className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Vessel Emission Analytics
              </h2>
              <p className="text-gray-600 text-sm">
                Detailed per-vessel emission calculations and methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">

            {/* Fuel Factors Information */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">{fuelFactorsInfo.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{fuelFactorsInfo.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {fuelFactorsInfo.factors.map((fuel, index) => (
                  <div key={index} className="p-3 bg-white rounded border border-green-100">
                    <h4 className="font-semibold text-gray-700 text-sm">{fuel.fuel} Factors</h4>
                    <p className="text-gray-600 text-xs mt-1 font-mono">{fuel.factors}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Emission Calculations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {emissionData.map((item) => (
                <div
                  key={item.id}
                  className={`p-5 rounded-lg border ${item.borderColor} ${item.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {item.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <p className={`font-mono text-sm ${item.textColor}`}>
                        {item.formula}
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                    <p className={`text-sm ${item.textColor} italic`}>
                      {item.calculation}
                    </p>
                  </div>

                  {item.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {item.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                    <p className="text-sm text-gray-600">
                      {item.description}
                    </p>
                  </div>

                  {item.additionalInfo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-1">Additional Information:</p>
                      <p className="text-sm text-gray-600 italic">
                        {item.additionalInfo}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-3">Key Data Collections Used:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">VesselPerformance</p>
                  <p>Fuel consumption data: HFO, LFO, MGO, shorePowerEnergy</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">VesselEmissionRawData</p>
                  <p>SOₓ, NOₓ, PM data with sulfur content and consumption details</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="font-semibold mb-1">FuelTable</p>
                  <p>Emission factors (CF_C02, CF_CH4, CF_N20), LCV, WtT factors</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselEmissionAnalytics;